/*
 * Admin Refund Retry · Operator Recovery Lane · Feb 2026
 * ======================================================
 *
 * Quiet recovery surface for failed Stripe refunds. Every truth
 * claim comes from the backend — this component never infers
 * success from a click or an HTTP 200.
 *
 * Wording rules (Truth Boundary Doctrine):
 *   • "Retry refund"       — button label (intent only).
 *   • "Retrying refund…"   — while the request is active.
 *   • "Refund created"     — ONLY after backend `status === "succeeded"`.
 *   • "Retry failed · <msg>" — when backend `status === "failed"`.
 * Never: "Refunded", or claims that money reached the customer or bank.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { RefreshCcw, AlertTriangle } from "lucide-react";
import { useTruthState, TRUTH_STATE_NEEDS_INPUT } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";

const API = process.env.REACT_APP_BACKEND_URL || "";

function fmtPence(pence) {
  const p = Number(pence || 0);
  return `£${(p / 100).toFixed(2)}`;
}

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminRefundRetry() {
  const [rows, setRows] = useState([]);
  const [loadState, setLoadState] = useState("loading"); // loading | ok | error
  const [inflight, setInflight] = useState({});          // payment_id → true
  // Batch P1 · Operations Truth-State — canonical async receipt.
  // Stripe refund creation returns `succeeded` when the Stripe
  // *object* exists — actual funds movement to the customer is not
  // authoritative until the async settlement callback lands. That
  // path maps to `waiting`, never `completed`.
  const truth = useTruthState();
  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const r = await axios.get(
        `${API}/api/operator/refunds/failed`,
        { withCredentials: true },
      );
      setRows(Array.isArray(r?.data?.items) ? r.data.items : []);
      setLoadState("ok");
    } catch {
      setLoadState("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retry = async (paymentId) => {
    if (inflight[paymentId]) return;
    setInflight((s) => ({ ...s, [paymentId]: true }));
    try {
      await truth.run(
        async () => {
          const r = await axios.post(
            `${API}/api/operator/refunds/${paymentId}/retry`,
            {},
            { withCredentials: true },
          );
          const status = r?.data?.status;
          if (status === "succeeded") {
            // Stripe accepted the refund creation — the Stripe object
            // exists but the actual funds movement to the customer's
            // card / bank is NOT authoritative until Stripe's async
            // settlement lands. `waiting`, not `completed`.
            truth.setState(
              "waiting",
              `Refund created — Stripe refund ${r.data.refund_provider_id ?? ""} · retry #${r.data.refund_retry_count ?? 1}. Waiting for provider settlement callback.`,
            );
          } else if (status === "already_refunded") {
            truth.setState(
              TRUTH_STATE_NEEDS_INPUT,
              "Already refunded — this payment already carries an authoritative refund.",
            );
          } else if (status === "already_in_progress") {
            truth.setState(
              "waiting",
              "Retry in progress — another operator is already retrying this refund.",
            );
          } else if (status === "not_eligible") {
            truth.setState(
              TRUTH_STATE_NEEDS_INPUT,
              r?.data?.reason || "Not eligible — this record cannot be retried.",
            );
          } else {
            truth.setState(
              "failed",
              r?.data?.failure_message || "Retry failed — Stripe rejected the refund.",
            );
          }
          // Always reload so the row leaves the unresolved lane only
          // if the backend removed it (i.e. succeeded), and stays
          // visible otherwise.
          await load();
          return r?.data;
        },
        {
          failedMessage: "Retry failed — network or server error.",
        },
      );
    } catch { /* truth.run captured */ }
    setInflight((s) => {
      const n = { ...s };
      delete n[paymentId];
      return n;
    });
  };

  return (
    <div data-testid="admin-refund-retry" className="min-h-dvh bg-slate-50 dark:bg-slate-950 p-4">
      <div className="max-w-3xl mx-auto space-y-4">
        <header className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-extrabold text-slate-900 dark:text-white">Refund Retry</h1>
            <p className="text-xs text-slate-500">Unresolved failed Stripe refunds. Retry uses the canonical service.</p>
          </div>
          <button
            data-testid="refund-retry-reload"
            onClick={load}
            className="p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-100"
            aria-label="Reload"
          >
            <RefreshCcw className="w-4 h-4" />
          </button>
        </header>

        <TruthReceipt
          state={truth.state}
          message={truth.message}
          onRetry={() => truth.setState(null)}
          onDismiss={() => truth.setState(null)}
        />

        {loadState === "loading" && (
          <p data-testid="refund-retry-loading" className="text-sm text-slate-500">Loading unresolved refunds…</p>
        )}
        {loadState === "error" && (
          <p data-testid="refund-retry-load-error" className="text-sm text-rose-600">
            Couldn&apos;t load unresolved refunds. Please refresh to try again.
          </p>
        )}
        {loadState === "ok" && rows.length === 0 && (
          <p data-testid="refund-retry-empty" className="text-sm text-slate-500">No unresolved failed refunds.</p>
        )}

        {loadState === "ok" && rows.length > 0 && (
          <ul className="space-y-3" data-testid="refund-retry-list">
            {rows.map((row) => {
              const busy = !!inflight[row.payment_id];
              return (
                <li
                  key={row.payment_id}
                  data-testid={`refund-retry-row-${row.payment_id}`}
                  className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-900 dark:text-white truncate">
                        {row.customer_name || "Unknown customer"}
                        <span className="ml-2 text-xs text-slate-500 font-normal">
                          · {row.product || "passport add-on"}
                        </span>
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 font-mono truncate">
                        {row.payment_id}
                      </p>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-xs">
                        <dt className="text-slate-500">Original</dt>
                        <dd className="text-slate-900 dark:text-white text-right font-medium">
                          {fmtPence(row.original_amount_pence)}
                        </dd>
                        <dt className="text-slate-500">Refund amount</dt>
                        <dd className="text-slate-900 dark:text-white text-right font-medium">
                          {fmtPence(row.refund_amount_pence || row.original_amount_pence)}
                        </dd>
                        <dt className="text-slate-500">Failed at</dt>
                        <dd className="text-slate-900 dark:text-white text-right">
                          {fmtWhen(row.refund_failed_at)}
                        </dd>
                        <dt className="text-slate-500">Retry count</dt>
                        <dd className="text-slate-900 dark:text-white text-right">
                          {row.refund_retry_count}
                        </dd>
                      </dl>
                      {row.refund_failure_message && (
                        <p
                          data-testid={`refund-retry-failure-message-${row.payment_id}`}
                          className="text-xs mt-2 text-rose-600 bg-rose-50 dark:bg-rose-900/20 px-2 py-1 rounded"
                        >
                          {row.refund_failure_message}
                        </p>
                      )}
                    </div>
                    <button
                      data-testid={`refund-retry-btn-${row.payment_id}`}
                      onClick={() => retry(row.payment_id)}
                      disabled={busy}
                      className="shrink-0 px-3 py-2 rounded-lg text-xs font-semibold bg-slate-900 text-white hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
                    >
                      {busy ? "Retrying refund…" : "Retry refund"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
